import type {
  InvoiceRequest,
  InvoiceRequestData,
} from '@finance-review/models/invoice';
import { isRequestError } from 'src/core/api/queryError';
import { type QueryState } from 'src/core/api/queryState';

import { useFetchDraftInvoiceRequest } from './useFetchDraftInvoiceRequest';

type Filters =
  | {
      requestState?: ('draft' | 'pending' | 'approved')[];
    }
  | undefined;

type Options = {
  requestId?: InvoiceRequest.EntityId;
  isEnabled?: boolean;
  filters?: Filters;
};
/**
 * useFetchDraftInvoiceRequest throws an error if the invoice is not found (404).
 * This hook returns a success state with undefined if the invoice is not found.
 */
export const useOptionalDraftInvoiceRequest = ({
  requestId,
  isEnabled = true,
  filters,
}: Options = {}): QueryState<InvoiceRequestData | undefined, unknown> => {
  const shouldFetch = Boolean(requestId) && isEnabled;

  const state = useFetchDraftInvoiceRequest(requestId, {
    isEnabled: shouldFetch,
    filters,
  });

  if (!shouldFetch) {
    return { status: 'success', data: undefined };
  }

  if (
    state.status === 'error' &&
    isRequestError(state.error) &&
    state.error.status === 404
  ) {
    return { status: 'success', data: undefined };
  }

  return state as QueryState<InvoiceRequestData | undefined, unknown>;
};
